import React, {useMemo, useRef, useState} from 'react';
import layout from '../marketing/marketing.module.css';
import styles from './CreateDialog.module.css';
import useFocusTrap from './useFocusTrap';
import {UploadIcon, CheckIcon, CloseIcon, HelpIcon, ArrowRightIcon, TrashIcon} from '../marketing/icons/Icons';
import {HELP_ROUTE} from '../marketing/routes';
import {Link} from 'react-router-dom';

const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return null;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const filesToChangeEvent = (fileList) => ({target: {files: fileList}});

/**
 * "Create a new page": a centred modal on desktop, a bottom sheet on mobile.
 * Pure UI layer over Page.js's existing create-flow state and handlers —
 * file validation, the actual create/save call, and the data model are
 * untouched; see APP.md.
 */
const CreateDialog = ({
    open,
    imagePresent,
    backgroundFileName,
    backgroundFileSize,
    backgroundImageDataUrl,
    title,
    error,
    ready,
    onTitleChange,
    onFileSelected,
    onRemoveBackground,
    onCancel,
    onSubmit,
}) => {
    const [dragActive, setDragActive] = useState(false);
    const dialogRef = useRef(null);
    const fileInputRef = useRef(null);
    const browseButtonRef = useRef(null);
    const mobileBrowseButtonRef = useRef(null);
    // Stable array identity: refs themselves never change, only re-create
    // the wrapping array once so the trap's initial-focus effect doesn't
    // re-run (and steal focus back) on every re-render.
    const initialFocusRefs = useMemo(() => [browseButtonRef, mobileBrowseButtonRef], []);

    useFocusTrap(open, dialogRef, initialFocusRefs);

    if (!open) return null;

    const openPicker = () => fileInputRef.current?.click();

    const handleDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        if (e.dataTransfer.files?.length) {
            onFileSelected(filesToChangeEvent(e.dataTransfer.files));
        }
    };

    const handleClose = () => {
        if (imagePresent || (title && title.trim())) {
            if (!window.confirm('Discard this new page?')) return;
        }
        onCancel();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            handleClose();
        }
    };

    const fileSize = formatFileSize(backgroundFileSize);

    return (
        <div className={styles.scrim} onKeyDown={handleKeyDown}>
            <section
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="create-title"
                className={styles.dialog}
            >
                <div className={styles.dragHandleRow}>
                    <span className={styles.dragHandle}/>
                </div>

                <header className={styles.header}>
                    <div>
                        <h2 id="create-title" className={styles.title}>Create a new page</h2>
                        <p className={styles.subtitle}>Start from a diagram of the feature you're planning.</p>
                    </div>
                    <button type="button" aria-label="Close" className={styles.closeButton} onClick={handleClose}>
                        <CloseIcon width={18} height={18}/>
                    </button>
                </header>

                <div className={styles.body}>
                    <div className={styles.step}>
                        <div className={styles.stepLabel}>
                            <span className={styles.stepNumber}>1</span>
                            <span className={styles.stepText}>Background</span>
                        </div>

                        {!imagePresent && (
                            <>
                                <div
                                    className={`${layout.dtOnly} ${styles.dropzone} ${dragActive ? styles.dropzoneActive : ''}`}
                                    onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                                    onDragLeave={() => setDragActive(false)}
                                    onDrop={handleDrop}
                                >
                                    <span className={styles.uploadIconWell}><UploadIcon/></span>
                                    <div className={styles.dropzoneText}>
                                        Drop your diagram here, or{' '}
                                        <button ref={browseButtonRef} type="button" className={styles.browseLink}
                                                onClick={openPicker}>
                                            browse files
                                        </button>
                                    </div>
                                    <div className={styles.helperText} id="background-help">
                                        A sequence diagram, an architecture diagram, a photo of the whiteboard. Any
                                        image that shows the feature.
                                    </div>
                                </div>

                                <button
                                    ref={mobileBrowseButtonRef}
                                    type="button"
                                    className={`${layout.mbOnly} ${styles.mobileDropzone}`}
                                    onClick={openPicker}
                                >
                                    <span className={styles.uploadIconWell}><UploadIcon/></span>
                                    <span className={styles.dropzoneText}>Choose a diagram</span>
                                    <span className={styles.helperText}>
                                        A sequence diagram, an architecture diagram, a photo of the whiteboard. Any
                                        image that shows the feature.
                                    </span>
                                </button>
                            </>
                        )}

                        {imagePresent && (
                            <div className={styles.filePreview}>
                                <div className={styles.thumbWell}>
                                    {backgroundImageDataUrl && (
                                        <img src={backgroundImageDataUrl} alt="" className={styles.thumbImg}/>
                                    )}
                                </div>
                                <div className={styles.fileInfo}>
                                    <div className={styles.fileMeta}>
                                        <div className={styles.fileName}>
                                            <CheckIcon className={styles.fileCheck}/>
                                            {backgroundFileName || 'Background image'}
                                        </div>
                                        <div className={styles.fileSize}>
                                            {fileSize ? `${fileSize} · Ready to use` : 'Ready to use'}
                                        </div>
                                    </div>
                                    <div className={styles.fileActions}>
                                        <button type="button" className={styles.replaceButton} onClick={openPicker}>
                                            Replace
                                        </button>
                                        <button type="button" className={`${layout.dtOnly} ${styles.removeButton}`}
                                                onClick={onRemoveBackground}>
                                            Remove
                                        </button>
                                        <button type="button" className={`${layout.mbOnly} ${styles.removeIconButton}`}
                                                aria-label="Remove background" onClick={onRemoveBackground}>
                                            <TrashIcon/>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className={styles.hiddenInput}
                            aria-hidden="true"
                            tabIndex={-1}
                            onChange={onFileSelected}
                            data-testid="createPageSelectBackground"
                        />

                        {error && (
                            <p id="background-error" className={styles.error} role="alert">{error}</p>
                        )}
                    </div>

                    <div className={styles.step}>
                        <label htmlFor="page-name" className={styles.stepLabel}>
                            <span className={styles.stepNumber}>2</span>
                            <span className={styles.stepText}>Page name</span>
                        </label>
                        <input
                            id="page-name"
                            type="text"
                            value={title || ''}
                            onChange={onTitleChange}
                            placeholder="e.g. Checkout revamp — payment flow"
                            aria-describedby="page-name-help"
                            className={styles.nameInput}
                        />
                        <p id="page-name-help" className={styles.helperText}>
                            A descriptive name makes the page easier to find later.
                        </p>
                    </div>
                </div>

                <footer className={styles.footer}>
                    <Link to={HELP_ROUTE} className={styles.howLink}>
                        <HelpIcon width={16} height={16}/>How pages work
                    </Link>
                    <div className={styles.footerButtons}>
                        <button type="button" className={styles.cancelButton} onClick={handleClose}>Cancel</button>
                        <button
                            type="button"
                            className={styles.createButton}
                            aria-disabled={!ready}
                            aria-describedby={!ready ? 'create-disabled-reason' : undefined}
                            onClick={() => ready && onSubmit()}
                        >
                            Create page<ArrowRightIcon width={16} height={16}/>
                        </button>
                        {/* A native `title` tooltip both fails on touch devices and, in
                            this a11y-tree implementation, ends up overriding the button's
                            accessible name instead of just describing it - so the
                            "what's missing" explanation is wired via aria-describedby
                            onto a visually-hidden node instead. */}
                        <span id="create-disabled-reason" className={styles.visuallyHidden}>
                            Add a background and a name to continue
                        </span>
                    </div>
                </footer>
            </section>
        </div>
    );
};

export default CreateDialog;
